// eslint-disable-next-line no-unused-vars
import { FlipProp, IconProp, PullProp, RotateProp, SizeProp, Transform } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import PropTypes from 'prop-types'
import { klass } from 'utils'

/**
 * Simplifies some common usage of FontAwesomeIcon (including the long default component name and frequent color options).
 * Also includes some helper components for layered and slashed icons: {@link Icon.Layers} and {@link Icon.Slashed}.
 *
 * @example `<Icon muted far i="money-check-edit"/>`
 * @example `<Icon success i="phone" size="2x"/>`
 *
 * @param far {boolean} Uses the "regular" package of icons (hollowed, most of the time) instead of the default solid
 * @param i {string|string[]|IconProp} The icon. If this is a regular (instead of solid) icon, you can just pass the `far` prop instead of turning this into an array
 * @param icon {string|string[]|IconProp} The icon, but without the abbreviated name for BC - `i` still takes priority
 * @param className {string}
 * @param props {{ primary: boolean, secondary: boolean, light: boolean, dark: boolean, muted: boolean, info: boolean, success: boolean, warning: boolean, danger: boolean, mask: string|string[], title: string ,spin: boolean, pulse: boolean, border: boolean, fixedWidth: boolean, inverse: boolean, listItem: boolean, flip: FlipProp, size: SizeProp, pull: PullProp, rotation: RotateProp, transform: Transform }} A bunch of `text-*` simplifiers, and any other prop that {@link FontAwesomeIcon} accepts
 * @constructor
 */
export default function Icon({ i, icon = '', far = false, className = '', ...props }) {
	icon = i || icon
	if (typeof icon == 'string' && far) {
		icon = ['far', icon]
	}

	const color      = ['primary', 'secondary', 'light', 'dark', 'muted', 'info', 'success', 'warning', 'danger'].find(c => props[c])
	const colorClass = `text-${color}`

	return <FontAwesomeIcon icon={icon} {...klass(className, colorClass)} {...props} />
}

//if reuse is needed, the value must be separate, otherwise it crashes in production
const iconPropType = PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)])
Icon.propTypes = {
	icon     : iconPropType,
	i        : PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
	far      : PropTypes.bool,
	className: PropTypes.string,
}

/**
 * Just a cleaner way to create a fa-layers element.
 * @see https://fontawesome.com/v5.15/how-to-use/on-the-web/styling/layering
 * @param children {Array<Icon|FontAwesomeIcon>} Children should be (FA)Icon elements only
 * @return {JSX.Element}
 */
Icon.Layers = ({ children }) => <span className="fa-layers">{children}</span>
Icon.Layers.propTypes = {
	children: PropTypes.arrayOf(PropTypes.shape({ type: PropTypes.oneOf([Icon, FontAwesomeIcon]) })),
}

//TODO create another component for stacks

Icon.Slashed = ({ icon, ...props }) => (
	<Icon.Layers>
		<Icon fixedWidth i={icon} {...props}/>
		<Icon fixedWidth i="slash"/>
		<Icon fixedWidth i="slash"/>{/* FIXME probably needs to replicate some props from the main icon */}
	</Icon.Layers>
)
Icon.Slashed.propTypes = {
	icon: iconPropType,
}
